/* eslint-disable react/no-unused-prop-types */
import { faBell } from '@fortawesome/pro-solid-svg-icons/faBell';
import { faEraser } from '@fortawesome/pro-solid-svg-icons/faEraser';
import { faMinus } from '@fortawesome/pro-solid-svg-icons/faMinus';
import { faPencilAlt } from '@fortawesome/pro-solid-svg-icons/faPencilAlt';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';
import { faTrashAlt } from '@fortawesome/pro-solid-svg-icons/faTrashAlt';
import { SavedSearch } from '@propertypal/shared/src/types/search';
import platform from '@propertypal/shared/src/utils/platform';
import ButtonPillRow from '@propertypal/web-ui/src/buttons/ButtonPillRow';
import FontAwesomeIcon from '@propertypal/web-ui/src/icons/FontAwesomeIcon';
import { openTooltip } from '@propertypal/web-ui/src/notifications/Tooltip';
import React, { CSSProperties, FunctionComponent, useState } from 'react';
import { useTheme } from 'styled-components';
import { getMapInstance, getZoom } from './GoogleMap';
import {
  ControlsContainer,
  Control,
  TypeContainer,
  SavedSearchButton,
  DrawControls,
  ZoomControls,
} from './MapControls.style';

interface Props {
  zoomControl?: boolean;
  mapTypeControl?: boolean;
  mapTypeControlPosition?: 'top-right' | 'bottom-center';
  drawingActive?: boolean;
  eraseActive?: boolean;
  travelTimeActive?: boolean;
  onDrawClick?: () => void;
  onEraseClick?: () => void;
  onTrashClick?: () => void;
  onTravelTimeClick?: () => void;
  savable?: boolean;
  disableSaveSearch?: boolean;
  onSaveSearchClick?: () => void;
  savedSearch?: SavedSearch['search'];
  containerStyle?: CSSProperties;
  listUrl?: string;
}

const MapControls: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const [type, setType] = useState('roadmap');

  const handleZoom = (delta: number) => {
    const map = getMapInstance();

    if (map) {
      map.setZoom(getZoom() + delta);
    }
  };

  const handleTypeChange = (nextType: string) => {
    const map = getMapInstance();

    if (map) {
      setType(nextType);
      map.setMapTypeId(nextType);
    }
  };

  const buttons: any[] = [
    { text: 'Map', onClick: () => handleTypeChange('roadmap'), active: type === 'roadmap' },
    {
      text: 'Satellite',
      onClick: () => handleTypeChange('hybrid'),
      active: type === 'hybrid',
      testID: 'satelliteTypeButton',
    },
  ];

  if (props.listUrl) {
    buttons.unshift({ text: 'List', href: props.listUrl });
  }

  return (
    <>
      {props.mapTypeControl && (
        <TypeContainer aria-label="Map terrain options" $position={props.mapTypeControlPosition}>
          <ButtonPillRow buttons={buttons} />
        </TypeContainer>
      )}

      <ControlsContainer $typePosition={props.mapTypeControlPosition} style={props.containerStyle}>
        {props.zoomControl && (
          <ZoomControls>
            <Control aria-label="Zoom in" onClick={() => handleZoom(1)} data-testid="zoomInButton" type="button">
              <FontAwesomeIcon icon={faPlus} color={theme.textDark} style={{ fontSize: 20 }} />
            </Control>

            <Control aria-label="Zoom out" onClick={() => handleZoom(-1)} data-testid="zoomOutButton" type="button">
              <FontAwesomeIcon icon={faMinus} color={theme.textDark} style={{ fontSize: 20 }} />
            </Control>
          </ZoomControls>
        )}

        {(props.onDrawClick || props.onEraseClick || props.onTrashClick) && (
          <DrawControls data-testid="draw-controls">
            {props.onDrawClick && (
              <Control
                aria-label="Draw a search"
                $active={props.drawingActive}
                onClick={props.onDrawClick}
                data-testid="drawButton"
                type="button"
              >
                <FontAwesomeIcon icon={faPencilAlt} style={{ fontSize: 20 }} />
              </Control>
            )}

            {props.onEraseClick && (
              <Control
                aria-label="Erase search"
                $active={props.eraseActive}
                onClick={props.onEraseClick}
                data-testid="eraseButton"
                type="button"
              >
                <FontAwesomeIcon icon={faEraser} style={{ fontSize: 20 }} />
              </Control>
            )}

            {props.onTrashClick && (
              <Control
                aria-label="Delete drawn search"
                onClick={props.onTrashClick}
                data-testid="trashButton"
                type="button"
              >
                <FontAwesomeIcon icon={faTrashAlt} style={{ fontSize: 20 }} />
              </Control>
            )}
          </DrawControls>
        )}
      </ControlsContainer>

      {props.savable && props.onSaveSearchClick && (
        <SavedSearchButton
          onClick={() => {
            if (props.onSaveSearchClick && !props.disableSaveSearch) {
              props.onSaveSearchClick();
            }
          }}
          data-testid="saveSearchButton"
          disableSaveSearch={props.disableSaveSearch}
          onMouseEnter={(e) => {
            if (props.disableSaveSearch) {
              openTooltip(
                'You must click the "Search" button in the search form to update your results before you can Save this search.',
                e,
              );
            }
          }}
        >
          <FontAwesomeIcon icon={faBell} />
          {platform.select(
            props.savedSearch ? 'Edit saved search' : 'Save this search',
            props.savedSearch ? 'Edit Alert' : 'Create Alert',
          )}
        </SavedSearchButton>
      )}
    </>
  );
};

export default MapControls;
